import { Ref } from "react";
import styled from "styled-components";
import { Season } from "../../../models/season.model";
import { SetState, StatePair } from "../../../types/SetState";
import { StepOne } from "./steps/step-one.step";
import { StepFour } from "./steps/step-four.step";
import { Poster } from "../../../types/Poster";
import { Subject } from "rxjs";
import { StepTwo } from "./steps/step-two.step";
import { StepThree } from "./steps/step-three.step";
import { FormInstance } from "antd";
import { StepFive } from "./steps/step-five.step";
import { ReviewStep } from "./steps/review.step";
import { SeriesProfile } from "../../../models/series-profile.model";
import { ValidationResult } from "../utils/validation";
import { StepCompletionStatus } from "../../films/components/step-nav.component";

const StepZeroStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  width: 100%;
`;

export const Step = ({
  form,
  currentStep,
  setCurrentStep,
  seasons,
  setSeasons,
  activeSeasonIndex,
  setActiveSeasonIndex,
  verticalPoster,
  setVerticalPoster,
  horizontalPoster,
  setHorizontalPoster,
  synopsis,
  setSynopsis,
  nameOptions,
  professionalProfileSubject$,
  title,
  setTitle,
  customUrlSubject$,
  customUrlInputRef,
  customUrl,
  setCustomUrl,
  urlVerified,
  showVerification,
  onSeriesTitleChange,
  titleFieldsAutoFocus,
  completedSteps,
  posterErrors,
  seasonPosterErrors,
  episodePosterErrors,
  setPosterErrors,
  setSeasonPosterErrors,
  setEpisodePosterErrors,
  stepFourTab,
  setStepFourTab,
  stepFiveTab,
  setStepFiveTab,
  activeEpisodeIndex,
  setActiveEpisodeIndex,
  stepFourShowErrors,
  setStepFourShowErrors,
  onValidate,
}: {
  completedSteps: StepCompletionStatus[];
  nameOptions: {
    value: string;
    label: string;
    logo?: string;
    id?: string;
    full_name?: string;
    custom_url?: string;
    type?: "professional" | "business";
  }[];
  professionalProfileSubject$: Subject<string>;
  form: FormInstance<SeriesProfile>;
  customUrlSubject$: Subject<string>;
  customUrlInputRef: Ref<HTMLInputElement>;
  customUrl: string;
  urlVerified: boolean | null;
  showVerification: boolean;
  setCustomUrl: SetState<string>;
  onSeriesTitleChange?: (title: string) => void;
  titleFieldsAutoFocus?: boolean;
  onValidate?: (result: ValidationResult) => void;
} & StatePair<"currentStep", "setCurrentStep", number> &
  StatePair<"seasons", "setSeasons", Season[]> &
  StatePair<"activeSeasonIndex", "setActiveSeasonIndex", number> &
  StatePair<"verticalPoster", "setVerticalPoster", Poster[]> &
  StatePair<"horizontalPoster", "setHorizontalPoster", Poster[]> &
  StatePair<"synopsis", "setSynopsis", string> &
  StatePair<"title", "setTitle", string> &
  StatePair<"posterErrors", "setPosterErrors", [boolean, boolean]> &
  StatePair<
    "seasonPosterErrors",
    "setSeasonPosterErrors",
    [boolean, boolean][]
  > &
  StatePair<
    "episodePosterErrors",
    "setEpisodePosterErrors",
    [boolean, boolean][]
  > &
  StatePair<"stepFourTab", "setStepFourTab", 0 | 1> &
  StatePair<"stepFiveTab", "setStepFiveTab", 0 | 1 | 2> &
  StatePair<"activeEpisodeIndex", "setActiveEpisodeIndex", number> &
  StatePair<"stepFourShowErrors", "setStepFourShowErrors", boolean>) => {
  return (
    <>
      {currentStep === 0 ? (
        <StepZeroStack>
          <StepOne
            show
            {...{
              synopsis,
              setSynopsis,
              title,
              setTitle,
              form,
              customUrlSubject$,
              customUrlInputRef,
              customUrl,
              urlVerified,
              showVerification,
              setCustomUrl,
              onSeriesTitleChange,
              titleFieldsAutoFocus,
            }}
          />
          <StepTwo show />
        </StepZeroStack>
      ) : (
        <>
          <StepOne
            show={false}
            {...{
              synopsis,
              setSynopsis,
              title,
              setTitle,
              form,
              customUrlSubject$,
              customUrlInputRef,
              customUrl,
              urlVerified,
              showVerification,
              setCustomUrl,
              onSeriesTitleChange,
              titleFieldsAutoFocus,
            }}
          />
          <StepTwo show={false} />
        </>
      )}
      <StepThree
        show={currentStep === 1}
        {...{
          verticalPoster,
          setVerticalPoster,
          horizontalPoster,
          setHorizontalPoster,
          posterErrors,
        }}
      />
      <StepFour
        show={currentStep === 2}
        {...{
          seasons,
          setSeasons,
          activeSeasonIndex,
          setActiveSeasonIndex,
          nameOptions,
          professionalProfileSubject$,
          stepFourTab,
          setStepFourTab,
          stepFourShowErrors,
        }}
      />
      <StepFive
        show={currentStep === 3}
        {...{
          seasons,
          setSeasons,
          activeSeasonIndex,
          setActiveSeasonIndex,
          nameOptions,
          professionalProfileSubject$,
          stepFiveTab,
          setStepFiveTab,
          activeEpisodeIndex,
          setActiveEpisodeIndex,
        }}
      />
      {currentStep === 4 ? (
        <ReviewStep
          completedSteps={completedSteps}
          currentStep={currentStep}
          synopsis={synopsis}
          form={form}
          seasons={seasons}
          verticalPoster={verticalPoster}
          horizontalPoster={horizontalPoster}
          setCurrentStep={setCurrentStep}
          visible={currentStep === 4}
          setPosterErrors={setPosterErrors}
          setSeasonPosterErrors={setSeasonPosterErrors}
          setEpisodePosterErrors={setEpisodePosterErrors}
          activeSeasonIndex={activeSeasonIndex}
          setActiveSeasonIndex={setActiveSeasonIndex}
          stepFourTab={stepFourTab}
          setStepFourTab={setStepFourTab}
          setStepFourShowErrors={setStepFourShowErrors}
          setActiveEpisodeIndex={setActiveEpisodeIndex}
          onValidate={onValidate}
        />
      ) : null}
    </>
  );
};
